/**
 * flashbookReportImage.web.ts — capturing the report card in a browser.
 *
 * The same job as the device's own implementation beside this one, by entirely
 * different machinery: a browser has no view snapshotter, so the card is *re-drawn*
 * from its computed styles by the capture library and handed back as a `data:` URI
 * (which is also the one form the web's `uploadBlob` accepts from a generated
 * image). The size is read off the base64 payload — nothing has to be written to
 * disk to find out how large the upload will be, which is why the ladder here is
 * arithmetic rather than a file read.
 *
 * Note what is captured: the *card*, and the card of an untouched layout. The
 * sheet's own copy is scaled down with a transform so a phone's column can hold
 * it, and a snapshotter photographs the layout it is handed — which is why the
 * capture is taken of a second, unscaled host beside the preview rather than of
 * the picture the learner is looking at (see `FlashBookReportShareSheet`).
 *
 * What is asked for is a *plain* view, not the capture library's own component:
 * `captureRef` resolves a host view itself — the DOM node, here — and handing it
 * the library's own wrapper instance is the one shape a browser refuses, with
 * `Element is not attached to a Document`.
 */
import type {View} from 'react-native'

import {
  dataUriBytes,
  imageFitsBudget,
  REPORT_IMAGE_BUDGET,
  REPORT_IMAGE_SIZES,
} from '#/lib/flashbookReportCard'

/** The picture, as the thing that will be uploaded. */
export type CapturedReportImage = {
  uri: string
  width: number
  height: number
  bytes: number
  /**
   * Whether it is inside the image budget.
   *
   * Carried rather than assumed: a picture that is too large is refused by the
   * server, and a caller that has to find that out from a failed upload cannot
   * tell the learner *why* their report could not be posted.
   */
  fits: boolean
}

/** The one method this module needs from the capture library. */
type CaptureModule = {
  captureRef: (
    ref: React.RefObject<View | null>,
    options: {
      width: number
      height: number
      format: 'png'
      result: 'data-uri'
    },
  ) => Promise<string>
}

/**
 * The capture library, taken by the deep path the rest of the app imports.
 *
 * Loaded when a picture is actually wanted, and not with the reader: this is the
 * html-to-canvas renderer, and every page of every book would pay for it so that
 * one screen could share a report.
 */
async function captureModule(): Promise<CaptureModule> {
  // @ts-expect-error — the deep path is not a module to TypeScript; it is the one
  // the app imports a capture from (see the QR card, which is typed the same way).
  const mod = await import('react-native-view-shot/src/index')
  return mod
}

export async function captureReportImage(
  ref: React.RefObject<View | null>,
): Promise<CapturedReportImage> {
  const {captureRef} = await captureModule()
  let last: CapturedReportImage | null = null

  for (const size of REPORT_IMAGE_SIZES) {
    const uri = await captureRef(ref, {
      width: size.width,
      height: size.height,
      format: 'png',
      result: 'data-uri',
    })
    const bytes = dataUriBytes(uri)
    last = {...size, uri, bytes, fits: imageFitsBudget(bytes)}
    if (last.fits) return last
  }

  // Every step was too large: the smallest picture is still the best answer, and
  // whether it is postable is now the server's call rather than ours.
  if (last) return last
  throw new Error('Nothing was captured')
}

export {REPORT_IMAGE_BUDGET}
